import { makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import React from "react";
import {
	ArrayInput,
	BooleanInput,
	FormDataConsumer,
	SelectInput,
	TextInput,
	required,
	useTranslate,
} from "react-admin";
import { useField, useForm } from "react-final-form";

import SimpleFormIteratorEx from "../../../components/_extensions/react-admin/ra-ui-materialui/src/form/SimpleFormIteratorEx";


// Mirrored from backend/osfc/modules/oauth2/form_schema.py (incl. fieldTypeChoices) — keep in sync.
const NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_-]*$/;
const MAX_FIELDS = 20;
const MAX_OPTIONS = 50;
const MAX_NAME_LEN = 64;
const MAX_LABEL_LEN = 256;
const MAX_VALUE_LEN = 1024;

const RESERVED_NAMES = new Set([
	"confirmed", "form_schema", "csrf_token",
	"response_type", "client_id", "redirect_uri", "scope", "state",
	"code_challenge", "code_challenge_method", "nonce", "response_mode", "prompt",
	"grant_type", "code", "code_verifier",
]);

const fieldTypeChoices = [
	{ id: "text", name: "resources.third-party-apps.form_schema.types.text" },
	{ id: "number", name: "resources.third-party-apps.form_schema.types.number" },
	{ id: "select", name: "resources.third-party-apps.form_schema.types.select" },
	{ id: "checkbox", name: "resources.third-party-apps.form_schema.types.checkbox" },
	{ id: "hidden", name: "resources.third-party-apps.form_schema.types.hidden" },
];

const validateName = (val) => {
	if (!val) return undefined;
	if (val.length > MAX_NAME_LEN) {
		return "resources.third-party-apps.form_schema.errors.name_too_long";
	}
	if (!NAME_REGEX.test(val)) {
		return "resources.third-party-apps.form_schema.errors.invalid_name";
	}
	if (RESERVED_NAMES.has(val)) {
		return "resources.third-party-apps.form_schema.errors.reserved_name";
	}
};

const makeValidateUniqueName = (arrayPath) => (val, vals) => {
	if (!val) return undefined;
	const fields = arrayPath.split(".").reduce((obj, key) => obj?.[key], vals) || [];
	const count = fields.filter((f) => f && f.name === val).length;
	if (count > 1) {
		return "resources.third-party-apps.form_schema.errors.duplicate_name";
	}
};

const validateUniqueFormSchemaName = makeValidateUniqueName("form_schema");

const validateLabel = (val) => {
	if (val && val.length > MAX_LABEL_LEN) {
		return "resources.third-party-apps.form_schema.errors.label_too_long";
	}
};

const validateValue = (val) => {
	if (val && val.length > MAX_VALUE_LEN) {
		return "resources.third-party-apps.form_schema.errors.value_too_long";
	}
};

const validateNumber = (val) => {
	if (val == null || val === "") return undefined;
	if (Number.isNaN(Number(val))) {
		return "resources.third-party-apps.form_schema.errors.invalid_number";
	}
};

const validateMaxFields = (val) => {
	if (Array.isArray(val) && val.length > MAX_FIELDS) {
		return "resources.third-party-apps.form_schema.errors.too_many_fields";
	}
};

const validateMaxOptions = (val) => {
	if (Array.isArray(val) && val.length > MAX_OPTIONS) {
		return "resources.third-party-apps.form_schema.errors.too_many_options";
	}
};

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		marginTop: theme.spacing(2),
	},
	heading: {
		marginBottom: theme.spacing(1),
	},
	iteratorForm: {
		"& > .ra-input": {
			width: "100%",
		},
	},
	editor: {
		width: "100%",
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	row: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "flex-start",
		gap: theme.spacing(2),
	},
	typeInput: { minWidth: 140 },
	nameInput: { minWidth: 180 },
	labelInput: { minWidth: 200 },
	valueInput: { minWidth: 220 },
	toggle: { minWidth: 140 },
	optionsBlock: {
		marginTop: theme.spacing(1),
		paddingLeft: theme.spacing(2),
		borderLeft: `2px solid ${theme.palette.divider}`,
	},
}));


const FieldEditor = ({ scopedFormData, getSource, classes }) => {
	const form = useForm();
	const type = scopedFormData?.type;
	const isSelect = type === "select";
	const isCheckbox = type === "checkbox";
	const isHidden = type === "hidden";
	const isText = type === "text";
	const isNumber = type === "number";

	const optionChoices = isSelect
		? (scopedFormData.options || [])
			.filter((o) => o && o.value)
			.map((o) => ({ id: o.value, name: o.label || o.value }))
		: [];

	const handleTypeChange = () => {
		// Keep `name` — the developer-supplied identifier survives type change.
		// Everything else (label + type-specific data + toggles) resets.
		form.batch(() => {
			form.change(getSource("label"), undefined);
			form.change(getSource("value"), undefined);
			form.change(getSource("checked"), undefined);
			form.change(getSource("options"), undefined);
			form.change(getSource("required"), false);
			form.change(getSource("disabled"), false);
		});
	};

	const hasType = Boolean(type);

	return (
		<div className={classes.editor}>
			<div className={classes.row}>
				<SelectInput
					source={getSource("type")}
					label="resources.third-party-apps.form_schema.fields.type"
					choices={fieldTypeChoices}
					validate={required()}
					onChange={handleTypeChange}
					className={classes.typeInput}
				/>
				<TextInput
					source={getSource("name")}
					label="resources.third-party-apps.form_schema.fields.name"
					validate={[required(), validateName, validateUniqueFormSchemaName]}
					className={classes.nameInput}
				/>
				{hasType && (
					<>
						{!isHidden && (
							<TextInput
								source={getSource("label")}
								label="resources.third-party-apps.form_schema.fields.label"
								validate={[required(), validateLabel]}
								className={classes.labelInput}
							/>
						)}
						{isCheckbox && (
							<BooleanInput
								source={getSource("checked")}
								label="resources.third-party-apps.form_schema.fields.checked"
								className={classes.toggle}
							/>
						)}
						{(isText || isHidden || isNumber) && (
							<TextInput
								type={isNumber ? "number" : "text"}
								source={getSource("value")}
								label="resources.third-party-apps.form_schema.fields.value"
								validate={[
									...(isHidden || scopedFormData?.disabled ? [required()] : []),
									validateValue,
									...(isNumber ? [validateNumber] : []),
								]}
								className={classes.valueInput}
							/>
						)}
						{isSelect && optionChoices.length > 0 && (
							<SelectInput
								source={getSource("value")}
								label="resources.third-party-apps.form_schema.fields.value"
								choices={optionChoices}
								validate={required()}
								className={classes.valueInput}
							/>
						)}
						{!isHidden && !isSelect && (
							<BooleanInput
								source={getSource("required")}
								label="resources.third-party-apps.form_schema.fields.required"
								className={classes.toggle}
							/>
						)}
						{!isHidden && (
							<BooleanInput
								source={getSource("disabled")}
								label="resources.third-party-apps.form_schema.fields.disabled"
								className={classes.toggle}
							/>
						)}
					</>
				)}
			</div>
			{hasType && isSelect && (
				<div className={classes.optionsBlock}>
					<ArrayInput
						source={getSource("options")}
						label="resources.third-party-apps.form_schema.fields.options"
						validate={[required(), validateMaxOptions]}
					>
						<SimpleFormIteratorEx>
							<TextInput
								source="value"
								label="resources.third-party-apps.form_schema.fields.option_value"
								validate={[required(), validateValue]}
							/>
							<TextInput
								source="label"
								label="resources.third-party-apps.form_schema.fields.option_label"
								validate={validateLabel}
							/>
						</SimpleFormIteratorEx>
					</ArrayInput>
				</div>
			)}
		</div>
	);
};


const FormSchemaArray = (props) => {
	const classes = useStyles();
	// Subscribe only to the master toggle — re-render is triggered when it flips,
	// not on every keystroke in any sibling form input.
	const { input: { value: enabled } } = useField("form_schema_enabled", {
		subscription: { value: true },
	});
	if (!enabled) return null;
	return (
		<ArrayInput
			{...props}
			source="form_schema"
			label=""
			validate={validateMaxFields}
		>
			<SimpleFormIteratorEx classes={{ form: classes.iteratorForm }}>
				<FormDataConsumer>
					{({ scopedFormData, getSource }) => (
						<FieldEditor
							scopedFormData={scopedFormData}
							getSource={getSource}
							classes={classes}
						/>
					)}
				</FormDataConsumer>
			</SimpleFormIteratorEx>
		</ArrayInput>
	);
};


export const FormSchemaInput = (props) => {
	const classes = useStyles();
	const translate = useTranslate();
	return (
		<div className={classes.root}>
			<Typography variant="subtitle1" className={classes.heading}>
				{translate("resources.third-party-apps.form_schema.title")}
			</Typography>
			<BooleanInput
				source="form_schema_enabled"
				label="resources.third-party-apps.form_schema.enabled"
				helperText="resources.third-party-apps.form_schema.helpers.enabled"
			/>
			<FormSchemaArray {...props} />
		</div>
	);
};
